import React, { useState, useEffect } from 'react';
import { ChevronDown, Target, Clock, Shield } from 'lucide-react';
import { useTheme } from './utils/theme';

const SpecialTeamTab = () => {
    // Mock theme - you can replace with your actual useTheme hook
    const { theme } = useTheme();
    const isDarkMode = true; // OLED: always dark
    const [gameSituation, setGameSituation] = useState('All Game Situations');
    const [playTypes, setPlayTypes] = useState('All Play Types');
    const [season, setSeason] = useState('Season 2023-2024');
    const [gameSituationOpen, setGameSituationOpen] = useState(false);
    const [playTypesOpen, setPlayTypesOpen] = useState(false);
    const [seasonOpen, setSeasonOpen] = useState(false);

    const gameSituationOptions = ['All Game Situations', 'Close Games', 'Blowouts', 'Comebacks', 'Leading', 'Trailing'];
    const playTypesOptions = ['All Play Types', 'Isolation', 'Pick & Roll', 'Post-Up', 'Spot-Up', 'Transition'];
    const seasonOptions = ['Season 2023-2024', 'Season 2022-2023', 'Season 2021-2022', 'Last 30 Games', 'Last 10 Games'];

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            setGameSituationOpen(false);
            setPlayTypesOpen(false);
            setSeasonOpen(false);
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const themeStyles = {
        background: '#0f172a',
        cardBackground: '#1e293b',
        textPrimary: '#f1f5f9',
        textSecondary: '#94a3b8',
        border: '#334155',
        progressBackground: '#334155',
        selectBorder: '#334155',
        selectHover: '#475569'
    };

    const MetricCard = ({ title, metrics, icon }) => (
        <div
            style={{
                backgroundColor: themeStyles.cardBackground,
                borderRadius: '8px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                border: `1px solid ${themeStyles.border}`,
                position: 'relative'
            }}
        >
            <div className="flex items-center gap-2 mb-4">
                <span style={{
                    color: '#ffd700',
                    fontSize: '16px'
                }}>
                    {icon}
                </span>
                <h3 style={{
                    color: themeStyles.textPrimary,
                    fontSize: '16px',
                    fontWeight: '600',
                    letterSpacing: '-0.01em'
                }}>
                    {title}
                </h3>
            </div>

            <div className="space-y-4">
                {metrics.map((metric, index) => (
                    <div key={index}>
                        <div style={{
                            color: themeStyles.textSecondary,
                            fontSize: '12px',
                            marginBottom: '4px'
                        }}>
                            {metric.label}
                        </div>
                        <div style={{
                            color: metric.highlight ? '#ffd700' : themeStyles.textPrimary,
                            fontSize: '20px',
                            fontWeight: '700',
                            marginBottom: metric.showProgress ? '8px' : '0'
                        }}>
                            {metric.value}
                        </div>
                        {metric.showProgress && (
                            <div style={{
                                width: '100%',
                                height: '4px',
                                backgroundColor: themeStyles.progressBackground,
                                borderRadius: '2px',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    width: `${metric.progressValue || 80}%`,
                                    height: '100%',
                                    backgroundColor: '#ffd700',
                                    borderRadius: '2px',
                                    transition: 'width 0.3s ease'
                                }} />
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    const SectionTitle = ({ children }) => (
        <h2 style={{
            color: themeStyles.textPrimary,
            fontSize: '18px',
            fontWeight: '600',
            margin: '32px 0 20px 0',
            letterSpacing: '-0.01em'
        }}>
            {children}
        </h2>
    );

    const SimpleSelect = ({ value, options, onChange, isOpen, setIsOpen, highlighted = false }) => (
        <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    backgroundColor: themeStyles.cardBackground,
                    border: `1px solid ${themeStyles.selectBorder}`,
                    borderRadius: '4px',
                    padding: '8px 32px 8px 12px',
                    fontSize: '14px',
                    color: themeStyles.textPrimary,
                    fontWeight: '400',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minWidth: '160px',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s ease',
                    position: 'relative',
                    // borderBottom: highlighted ? '3px solid #ffd700' : undefined
                }}
                onMouseEnter={(e) => {
                    if (!isOpen) e.target.style.borderColor = themeStyles.selectHover;
                }}
                onMouseLeave={(e) => {
                    if (!isOpen) e.target.style.borderColor = themeStyles.selectBorder;
                }}
            >
                <span>{value}</span>
                <ChevronDown
                    size={14}
                    style={{
                        color: themeStyles.textSecondary,
                        position: 'absolute',
                        right: '8px',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}
                />
            </button>

            {isOpen && (
                <>
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 999
                        }}
                        onClick={() => setIsOpen(false)}
                    />
                    <div
                        style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: themeStyles.cardBackground,
                            border: `1px solid ${themeStyles.border}`,
                            borderRadius: '4px',
                            marginTop: '2px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
                            zIndex: 1000,
                            overflow: 'hidden'
                        }}
                    >
                        {options.map((option, index) => (
                            <button
                                key={index}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onChange(option);
                                    setIsOpen(false);
                                }}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    color: themeStyles.textPrimary,
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    fontWeight: '400',
                                    transition: 'background-color 0.15s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#334155';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                }}
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );

    const CircularProgress = ({ value, label, sublabel }) => (
        <div className="flex flex-col items-center">
            <div style={{ position: 'relative', width: '120px', height: '120px', marginBottom: '16px' }}>
                <svg width="120" height="120" style={{ transform: 'rotate(-90deg)' }}>
                    <circle
                        cx="60"
                        cy="60"
                        r="45"
                        stroke="none"
                        strokeWidth="0"
                        fill="none"
                    />
                    <circle
                        cx="60"
                        cy="60"
                        r="45"
                        stroke="#ffd700"
                        strokeWidth="3"
                        fill="none"
                        strokeDasharray="8 6"
                        strokeLinecap="round"
                    />
                </svg>
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center'
                }}>
                    <div style={{
                        color: '#ffd700',
                        fontSize: '32px',
                        fontWeight: '700'
                    }}>
                        {value}
                    </div>
                </div>
            </div>
            <div style={{
                color: themeStyles.textSecondary,
                fontSize: '12px',
                textAlign: 'center'
            }}>
                {sublabel}
            </div>
        </div>
    );

    const ProgressBar = ({ label, value, max = 10 }) => (
        <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
                <span style={{
                    color: themeStyles.textSecondary,
                    fontSize: '14px'
                }}>
                    {label}
                </span>
                <span style={{
                    color: themeStyles.textPrimary,
                    fontSize: '14px',
                    fontWeight: '600'
                }}>
                    {value}
                </span>
            </div>
            <div style={{
                width: '100%',
                height: '6px',
                backgroundColor: themeStyles.progressBackground,
                borderRadius: '3px',
                overflow: 'hidden'
            }}>
                <div style={{
                    width: `${(value / max) * 100}%`,
                    height: '100%',
                    backgroundColor: '#ffd700',
                    borderRadius: '3px',
                    transition: 'width 0.3s ease'
                }} />
            </div>
        </div>
    );

    return (
        <div style={{
            backgroundColor: themeStyles.background,
            // minHeight: '100vh',
            // padding: '32px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif'
        }}>
            <div style={{ margin: '0 auto' }}>
                {/* Header */}
                <div className="flex gap-4 mb-6">
                    <SimpleSelect
                        value={gameSituation}
                        options={gameSituationOptions}
                        onChange={setGameSituation}
                        isOpen={gameSituationOpen}
                        setIsOpen={setGameSituationOpen}
                    />
                    <SimpleSelect
                        value={playTypes}
                        options={playTypesOptions}
                        onChange={setPlayTypes}
                        isOpen={playTypesOpen}
                        setIsOpen={setPlayTypesOpen}
                    />
                    <SimpleSelect
                        value={season}
                        options={seasonOptions}
                        onChange={setSeason}
                        isOpen={seasonOpen}
                        setIsOpen={setSeasonOpen}
                        highlighted={true}
                    />
                </div>

                {/* Set-Piece Execution & Efficiency */}
                <SectionTitle>Set-Piece Execution & Efficiency</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <MetricCard
                        title="Inbound Plays"
                        icon="🎯"
                        metrics={[
                            { label: "Success Rate", value: "87.5%", highlight: true, showProgress: true, progressValue: 87.5 },
                            { label: "Points Generated", value: "14.1", highlight: true }
                        ]}
                    />
                    <MetricCard
                        title="Free Throw Situations"
                        icon="🎯"
                        metrics={[
                            { label: "Box-Out Rate", value: "92.1%", highlight: true, showProgress: true, progressValue: 92.1 },
                            { label: "Rebounds Off Miss", value: "3.3", highlight: true }
                        ]}
                    />
                    <MetricCard
                        title="After-Timeout Plays"
                        icon="⏰"
                        metrics={[
                            { label: "ATO Success", value: "78.9%", highlight: true, showProgress: true, progressValue: 78.9 },
                            { label: "Points Scored", value: "18.7", highlight: true }
                        ]}
                    />
                </div>

                {/* Critical Situation Performance */}
                <SectionTitle>Critical Situation Performance</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <MetricCard
                        title="Clutch Performance"
                        icon="⚡"
                        metrics={[
                            { label: "Points (Last 5 Min, <5 Diff)", value: "12.4", highlight: true },
                            { label: "Game-Winning Plays", value: "7" },
                            { label: "Clutch FG%", value: "54.2%" }
                        ]}
                    />
                    <MetricCard
                        title="End-of-Quarter Plays"
                        icon="⏰"
                        metrics={[
                            { label: "Buzzer Beater Attempts", value: "23" },
                            { label: "Success Rate", value: "8" },
                            { label: "Decision Making Score", value: "5.7/10", highlight: true }
                        ]}
                    />
                </div>

                {/* Niche Defensive Assignments & Disruptive Roles */}
                <SectionTitle>Niche Defensive Assignments & Disruptive Roles</SectionTitle>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <MetricCard
                        title="Full-Court Press"
                        icon="🔥"
                        metrics={[
                            { label: "Forced Turnovers", value: "21", highlight: true },
                            { label: "Engagement Rate", value: "69.3%", highlight: true, showProgress: true, progressValue: 69.3 }
                        ]}
                    />
                    <MetricCard
                        title="Zone Defense"
                        icon="🛡️"
                        metrics={[
                            { label: "Zone Disruptions", value: "34", highlight: true, showProgress: true, progressValue: 85 },
                            { label: "Pass Lane Denials", value: "12.4" }
                        ]}
                    />
                    <MetricCard
                        title="Screen Navigation"
                        icon="🔄"
                        metrics={[
                            { label: "Screen Efficiency", value: "84.7%", highlight: true, showProgress: true, progressValue: 84.7 },
                            { label: "Assists Off Screens", value: "3.1", highlight: true }
                        ]}
                    />
                </div>

                {/* Player Impact in Huddles & Communication */}
                <SectionTitle>Player Impact in Huddles & Communication</SectionTitle>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div
                        style={{
                            backgroundColor: themeStyles.cardBackground,
                            borderRadius: '8px',
                            padding: '32px',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                            border: `1px solid ${themeStyles.border}`,
                            textAlign: 'center'
                        }}
                    >
                        <h3 style={{
                            color: themeStyles.textPrimary,
                            fontSize: '16px',
                            fontWeight: '600',
                            marginBottom: '24px'
                        }}>
                            Communication Score
                        </h3>
                        <CircularProgress
                            value={8.5}
                            sublabel="Above Average Leadership"
                        />
                    </div>

                    <div
                        style={{
                            backgroundColor: themeStyles.cardBackground,
                            borderRadius: '8px',
                            padding: '32px',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                            border: `1px solid ${themeStyles.border}`
                        }}
                    >
                        <h3 style={{
                            color: themeStyles.textPrimary,
                            fontSize: '16px',
                            fontWeight: '600',
                            marginBottom: '24px'
                        }}>
                            Huddle Leadership Index
                        </h3>
                        <ProgressBar label="Vocal Leadership" value={9.0} />
                        <ProgressBar label="Strategic Input" value={7.5} />
                        <ProgressBar label="Team Morale" value={8.5} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SpecialTeamTab;